import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DataTable from "../../../Components/DataGrid/DataGrid";
import { notificationConfigurationApi } from "../../api/notification/notification.service";
import { useSearchParams } from "react-router-dom";
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, TextField, MenuItem, Select, FormControl, InputLabel, Box, Checkbox, ListItemText, Autocomplete } from "@mui/material";
import { AutocompleteStationListPop } from "../../../Components/AutocompleteStationListPop/AutocompleteStationListPop";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { memo, useMemo, useState } from "react";
import Loader from "../../../Components/Loader/Loader";
import NotificationManagementForm from "./NotificationManagementForm";
import toast from "react-hot-toast";
import { OverflowTip } from "../../../Components/Mcdo/helpers";
import { extractAllowedUserRoleList } from "../../../Utils/Utils";


const getModalProps = (): { fullWidth?: boolean; maxWidth: "sm" | "md" } => {
    return {
        maxWidth: "md",
        fullWidth: true,
    };
}

const getModalTitle = (modalMode: string | null) => {
    if (modalMode === "create") {
        return "Add New Notification Configuration";
    }

    if (modalMode === "edit") {
        return "Edit Notification Configuration";
    }
}

const ActionsColumns = (params: GridRenderCellParams) => {
    const [_, setSearchParams] = useSearchParams();

    function openEditModal() {
        setSearchParams(
            {
                modal: "edit",
                userid: params.row.userid,
            },
            { replace: true },
        );
    }

    return (
        <div className="flex items-center gap-2">
            <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openEditModal}>
                <EditIcon className="cursor-pointer" />
            </Tooltip>
        </div>
    );
};


const columns: GridColDef[] = [
    {
        field: "actions",
        headerName: "",
        type: "actions",
        renderCell: ActionsColumns,
        hideable: false,
        disableExport: true,
    },
    {
        field: "name",
        headerName: "Name",
        sortable: true,
        minWidth: 110,
        hideable: true,
        flex: 1,
        renderCell(params) {
            return params.row.first_name + " " + params.row.last_name;
        },
    },
    {
        field: "email",
        headerName: "Email",
        sortable: true,
        minWidth: 110,
        hideable: true,
        flex: 1,
    },
    {
        field: "mobileno",
        headerName: "Mobile No",
        sortable: true,
        minWidth: 110,
        hideable: true,
        flex: 1,
    },
    {
        field: "role_name",
        headerName: "Role",
        sortable: true,
        minWidth: 110,
        hideable: true,
        flex: 1,
    },
    {
        field: "notification_medium",
        headerName: "Notification Medium",
        sortable: true,
        minWidth: 110,
        hideable: true,
        flex: 1,
        renderCell(params) {
            return <NotificationMediumsCell params={params} />;
        },
    },
];

const { useGetNotificationConfigurationQuery, usePostNotificationConfigurationMutation, usePatchNotificationConfigurationMutation } = notificationConfigurationApi;

const NotificationMediumsCell = ({ params }: { params: GridRenderCellParams }) => {
    const [patchConfig] = usePatchNotificationConfigurationMutation();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState<any>(null);

    if (!params.value || !params.value?.length) {
        return <p>No Permissions Assigned</p>;
    }

    const handleConfirm = async () => {
        if (!selectedPermission) return;
        setConfirmOpen(false);

        const toast_id = toast.loading("Updating...");
        try {
            await patchConfig({
                body: {
                    enable: !selectedPermission.enabled,
                    id: selectedPermission.id,
                    mode: selectedPermission.type,
                    user_id: params.row.userid,
                },
            }).unwrap();
            toast.success("Updated Successfully", { id: toast_id });
        } catch (error) {
            console.error("NOTIFICATION_MANAGEMENT_PATCH_ERROR", error);
            toast.error("Unable to Submit Request", { id: toast_id });
        } finally {
            setSelectedPermission(null);
        }
    };

    return (
        <OverflowTip>
            {params.value?.map((permission: any) => (
                <Chip
                    key={permission.id || permission.type}
                    label={permission.type}
                    color={permission.enabled ? "success" : "default"}
                    icon={permission.enabled ? <CheckIcon /> : <CloseIcon />}
                    onClick={() => {
                        setSelectedPermission(permission);
                        setConfirmOpen(true);
                    }}
                    variant={permission.enabled ? "filled" : "outlined"}
                    sx={{
                        margin: "0 2px",
                        cursor: "pointer",
                        "&:hover": permission.enabled ? {
                            backgroundColor: "success.dark",
                            color: "white",
                            "& .MuiChip-icon": {
                                color: "white",
                            }
                        } : {}
                    }}
                />
            ))}
            
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onClick={(e) => e.stopPropagation()}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    Are you sure you want to {selectedPermission?.enabled ? "disable" : "enable"} the {selectedPermission?.type} notification medium?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirm} color={selectedPermission?.enabled ? "error" : "primary"}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </OverflowTip>
    );
};

function NotificationManagement() {
    const [pagination, setPagination] = useState({
        page: 0,
        limit: 10,
    })
    const [filters, setFilters] = useState<{ role_ids: string[], search_text: string }>({
        role_ids: [],
        search_text: "",
    });
    const [appliedFilters, setAppliedFilters] = useState<{ role_ids: string[], search_text: string }>({
        role_ids: [],
        search_text: "",
    });

    const allowedRoleIdList = useMemo(() => {
        let roleHierarchies = JSON.parse(localStorage.getItem("roleHierarchies") || "{}");
        let allowedRoleIdList = extractAllowedUserRoleList(roleHierarchies);
        return allowedRoleIdList.filter(({ value }: any) => value !== "admin");
    }, []);

    console.log(allowedRoleIdList, "allowedRoleIdList")
    const [editRecord] = usePostNotificationConfigurationMutation()
    const [patchRecord] = usePatchNotificationConfigurationMutation()
    const { data, isFetching } = useGetNotificationConfigurationQuery({
        offset: pagination.page * pagination.limit,
        limit: pagination.limit,
        role_ids: appliedFilters.role_ids.length > 0 ? appliedFilters.role_ids.join(",") : undefined,
        search_text: appliedFilters.search_text || undefined,
    });
    const [searchParams, setSearchParams] = useSearchParams();

    const modalMode = searchParams.get("modal");
    const userid = searchParams.get("userid") ?? "";

    const initial_values = useMemo(() => {
        const DEFAULT_INITIAL_VALUE = {
            userid: "",
            mediums: [],
        };
        if (modalMode !== "edit" && !userid) return DEFAULT_INITIAL_VALUE;

        const _notification_configuration = data?.items.find((notification_configuration) => notification_configuration.userid === userid);

        if (!_notification_configuration) return DEFAULT_INITIAL_VALUE;

        return {
            user_id: _notification_configuration.userid,
            mediums: (_notification_configuration.notification_medium || [])
                // ?.filter((medium: any) => medium.enabled)
                .map((medium: any) => medium.type),
        };
    }, [modalMode, userid, data]);

    const handleCloseModal = () => {
        setSearchParams({}, { replace: true }); // Removes all search params
    };

    async function handleFormSubmit(values: any) {
        const toast_id = toast.loading("Submitting...");
        try {
            if (modalMode === "edit") {
                await editRecord({ body: values }).unwrap();
            }
            toast.success("Success", { id: toast_id });
            // handleCloseModal();
        } catch (error) {
            console.error("NOTIFICATION_MANAGEMENT_ERROR", error);
            toast.error("Unable to Submit Request", { id: toast_id });
        }
    }

    const UserInfoComponent = memo(() => {
        const _notification_configuration = data?.items.find((notification_configuration) => notification_configuration.userid === userid);

        return (
            <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                    <p className="flex-1"><span className="font-bold">Name:</span> {_notification_configuration?.first_name} {_notification_configuration?.last_name}</p>
                    <p className="flex-1"><span className="font-bold">Email:</span> {_notification_configuration?.email}</p>
                </div>
                <div className="flex gap-2">
                    <p className="flex-1"><span className="font-bold">Mobile No:</span> {_notification_configuration?.mobileno}</p>
                    <p className="flex-1"><span className="font-bold">Role:</span> {_notification_configuration?.role_name}</p>
                </div>
            </div>
        );
    });

    const FormComponent = memo(() => {
        if (modalMode === "edit" && isFetching)
            return (
                <div style={{ height: "10rem" }}>
                    <Loader />
                </div>
            );

        return <NotificationManagementForm infoComponent={<UserInfoComponent />} defaultValues={initial_values} handleSubmit={handleFormSubmit} />;
    });


    const renderTags = (value: any[], getTagProps: any) => {
        if (value.length === 0) return null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Chip size="small" variant="outlined" label={value[0]?.value} {...getTagProps({ index: 0 })} />
                {value.length > 1 && (
                    <AutocompleteStationListPop title={value.map((opt) => opt?.value ?? '').join(', ')}>
                        <Chip size="small" label={`+${value.length - 1}`} />
                    </AutocompleteStationListPop>
                )}
            </div>
        );
    };

    const modal_props = useMemo(() => getModalProps(), []);
    const modal_title = getModalTitle(modalMode);

    return (
        <div style={{ height: "80vh" }}>
            {/* <Button variant="contained" onClick={() => { }} style={{ marginBottom: "0.5rem" }}>
                Create
            </Button> */}
            {(modalMode === "create" || modalMode === "edit" || modalMode === "delete") && (
                <Dialog open={true} onClose={handleCloseModal} {...modal_props}>
                    <DialogTitle className="flex items-center justify-between">
                        {modal_title}
                        <CloseIcon className="assets-info-popup-close-icon pointer" onClick={handleCloseModal} />
                    </DialogTitle>
                    <DialogContent>
                        <FormComponent />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Box sx={{
                display: 'flex',
                gap: 2,
                marginBottom: '1rem',
                alignItems: 'center',
                backgroundColor: "white",
                padding: "1rem",
                borderRadius: "0.5rem",
                boxShadow: 1
            }}>
                <TextField
                    label="User Name"
                    variant="outlined"
                    size="small"
                    value={filters.search_text}
                    onChange={(e) => setFilters({ ...filters, search_text: e.target.value })}
                />
                <Autocomplete
                    size="small"
                    multiple
                    disableCloseOnSelect
                    options={allowedRoleIdList}
                    value={allowedRoleIdList.filter((role: any) => filters.role_ids.includes(role.id))}
                    onChange={(_, newValue) => setFilters({ ...filters, role_ids: newValue.map((v: any) => v.id) })}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                    getOptionLabel={(opt: any) => opt.value || ''}
                    renderInput={(params) => <TextField {...params} label="Role" />}
                    renderTags={renderTags}
                    sx={{ minWidth: 200 }}
                />
                <Button
                    variant="contained"
                    onClick={() => {
                        setPagination({ ...pagination, page: 0 });
                        setAppliedFilters(filters);
                    }}
                >
                    Search
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setFilters({ role_ids: [], search_text: "" });
                        setAppliedFilters({ role_ids: [], search_text: "" });
                        setPagination({ ...pagination, page: 0 });
                    }}
                >
                    Clear
                </Button>
            </Box>

            <DataTable
                tableInfo={{
                    id: "notification-configuration-table",
                    label: "Notification Configuration",
                    requireAuthSign: false,
                    timestamp: { startTs: null, endTs: null },
                }}
                loading={isFetching}
                pageIndex={pagination.page}
                pageSize={pagination.limit}
                pageChange={(page: number) => {
                    setPagination({ ...pagination, page })
                }}
                pageSizeChange={(size: number) => {
                    setPagination({ ...pagination, limit: size })
                }}
                paginationMode="server"
                rows={data?.items || []}
                rowCount={data?.totalRecords || 0}
                columns={columns}
                showFilterButton={false}
                getRowId={(row) => row.userid}
            />

        </div>
    )
}

export default NotificationManagement;